import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Button, Stack, TextField, Alert, Paper, Typography,
  FormControl, InputLabel, Select, MenuItem, Chip, Box,
  Checkbox, FormControlLabel, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, LinearProgress,
  Card, CardContent, Grid, IconButton, Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StreamIcon from '@mui/icons-material/Stream';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { apiFetch, getApiBaseUrl } from '../helpers';

// All US States
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'Washington DC' }
];

interface ZillowProperty {
  _id: string;
  fullAddress: string;
  state: string;
  price: number | null;
  zestimate: number | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  homeType: string | null;
  isDeal: boolean;
  dealRatio: number | null;
  detailUrl: string | null;
  scrapedAt: string;
}

interface Progress {
  isRunning: boolean;
  currentState: string | null;
  currentPriceRange: string | null;
  currentPage: number;
  totalPages: number;
  totalListingsInRange: number;
  priceRangeIndex: number;
  totalPriceRanges: number;
  listingsFound: number;
  listingsSaved: number;
  dealsFound: number;
  errors: string[];
  nextRangeIn: number | null;  // Countdown seconds until next range
}

interface Stats {
  total: number;
  deals: number;
  byState: Array<{
    _id: string;
    count: number;
    deals: number;
    avgPrice: number;
    avgZestimate: number;
  }>;
}

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmt = (n?: number | null) => (typeof n === 'number' && n > 0 ? currency.format(n) : '—');

export default function ZillowFetcher() {
  // Filter state
  const [selectedStates, setSelectedStates] = useState<string[]>(['NY']);
  const [priceMin, setPriceMin] = useState(20000);
  const [priceMax, setPriceMax] = useState(600000);
  const [bedsMin, setBedsMin] = useState(3);
  const [sqftMin, setSqftMin] = useState(1000);

  // Exclusions
  const [excludeTownhouse, setExcludeTownhouse] = useState(true);
  const [excludeCondo, setExcludeCondo] = useState(true);
  const [excludeLand, setExcludeLand] = useState(true);
  const [excludeManufactured, setExcludeManufactured] = useState(true);
  const [excludeApartment, setExcludeApartment] = useState(true);
  const [excludeNewConstruction, setExcludeNewConstruction] = useState(true);
  const [excludeAuction, setExcludeAuction] = useState(true);
  const [excludeComingSoon, setExcludeComingSoon] = useState(true);

  // Price range splitting (for more coverage)
  const [usePriceRanges, setUsePriceRanges] = useState(true);

  // Skip proxy (use local IP instead)
  const [skipProxy, setSkipProxy] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [properties, setProperties] = useState<ZillowProperty[]>([]);
  const [testResults, setTestResults] = useState<any>(null);
  const [status, setStatus] = useState<{ message: string; type: 'info' | 'success' | 'error' | 'warning' } | null>(null);

  // View filter
  const [viewState, setViewState] = useState<string>('all');
  const [viewDealsOnly, setViewDealsOnly] = useState(false);

  // Live scrape (SSE) state
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveProgress, setLiveProgress] = useState<{
    currentPage: number;
    totalPages: number;
    totalResultCount: number;
    totalScraped: number;
  } | null>(null);
  const [liveListings, setLiveListings] = useState<any[]>([]);
  const eventSourceRef = useRef<EventSource | null>(null);

  // RapidAPI scrape state (no CAPTCHA)
  const [rapidApiLoading, setRapidApiLoading] = useState(false);
  const rapidApiEventSourceRef = useRef<EventSource | null>(null);

  // Fetch progress
  const fetchProgress = useCallback(async () => {
    try {
      const res = await apiFetch('/api/zillow/progress');
      const data = await res.json();
      setProgress(data);
      return data;
    } catch (err) {
      console.error('Failed to fetch progress:', err);
      return null;
    }
  }, []);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await apiFetch('/api/zillow/stats');
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  // Fetch properties
  const fetchProperties = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (viewState !== 'all') params.append('state', viewState);
      if (viewDealsOnly) params.append('isDeal', 'true');
      params.append('limit', '200');

      const res = await apiFetch(`/api/zillow/properties?${params.toString()}`);
      const data = await res.json();
      setProperties(data.properties || []);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
    }
  }, [viewState, viewDealsOnly]);

  // Poll progress while running
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (progress?.isRunning) {
      interval = setInterval(async () => {
        const p = await fetchProgress();
        if (!p?.isRunning) {
          fetchStats();
          fetchProperties();
        }
      }, 3000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [progress?.isRunning, fetchProgress, fetchStats, fetchProperties]);

  // Initial load
  useEffect(() => {
    fetchProgress();
    fetchStats();
    fetchProperties();
  }, [fetchProgress, fetchStats, fetchProperties]);

  // Refresh when view filters change
  useEffect(() => {
    fetchProperties();
  }, [viewState, viewDealsOnly, fetchProperties]);

  // Start scraping
  const startScrape = async () => {
    if (selectedStates.length === 0) {
      setStatus({ message: 'Please select at least one state', type: 'warning' });
      return;
    }

    setLoading(true);
    setStatus(null);

    try {
      const res = await apiFetch('/api/zillow/scrape', {
        method: 'POST',
        body: JSON.stringify({
          states: selectedStates,
          priceMin,
          priceMax,
          bedsMin,
          sqftMin,
          excludeTownhouse,
          excludeCondo,
          excludeLand,
          excludeManufactured,
          excludeApartment,
          excludeNewConstruction,
          excludeAuction,
          excludeComingSoon,
          usePriceRanges,
          skipProxy
        }),
      });

      const data = await res.json();

      if (res.ok) {
        const rangeMsg = usePriceRanges ? ' with price range splitting (19 ranges, 90s delay each)' : '';
        const proxyMsg = skipProxy ? ' (NO PROXY - using local IP)' : '';
        setStatus({ message: `Scrape started for ${selectedStates.join(', ')}${rangeMsg}${proxyMsg}`, type: 'success' });
        fetchProgress();
      } else {
        setStatus({ message: data.error || 'Failed to start scrape', type: 'error' });
      }
    } catch (err: any) {
      setStatus({ message: err.message || 'Failed to start scrape', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Stop scraping
  const stopScrape = async () => {
    try {
      await apiFetch('/api/zillow/stop', { method: 'POST' });
      setStatus({ message: 'Stop requested', type: 'info' });
      fetchProgress();
    } catch (err: any) {
      setStatus({ message: err.message || 'Failed to stop scrape', type: 'error' });
    }
  };

  // Test scrape (single state, shows results immediately)
  const testScrape = async () => {
    if (selectedStates.length === 0) {
      setStatus({ message: 'Please select a state to test', type: 'warning' });
      return;
    }

    setTestLoading(true);
    setTestResults(null);
    setStatus(null);

    try {
      const res = await apiFetch('/api/zillow/test-scrape', {
        method: 'POST',
        body: JSON.stringify({
          state: selectedStates[0],
          priceMin,
          priceMax,
          bedsMin,
          sqftMin,
          excludeTownhouse,
          excludeCondo,
          excludeLand,
          excludeManufactured,
          excludeApartment,
          excludeNewConstruction,
          excludeAuction,
          excludeComingSoon,
          saveToDb: false
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setTestResults(data);
        setStatus({ message: `Found ${data.total} listings, ${data.deals} deals`, type: 'success' });
      } else {
        setStatus({ message: data.error || 'Test failed', type: 'error' });
      }
    } catch (err: any) {
      setStatus({ message: err.message || 'Test failed', type: 'error' });
    } finally {
      setTestLoading(false);
    }
  };

  // Save test results to DB
  const saveTestResults = async () => {
    if (!testResults || testResults.listings?.length === 0) return;

    setTestLoading(true);

    try {
      const res = await apiFetch('/api/zillow/test-scrape', {
        method: 'POST',
        body: JSON.stringify({
          state: selectedStates[0],
          priceMin,
          priceMax,
          bedsMin,
          sqftMin,
          excludeTownhouse,
          excludeCondo,
          excludeLand,
          excludeManufactured,
          excludeApartment,
          excludeNewConstruction,
          excludeAuction,
          excludeComingSoon,
          saveToDb: true
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setStatus({ message: `Saved ${data.saved} properties to database`, type: 'success' });
        fetchStats();
        fetchProperties();
      } else {
        setStatus({ message: data.error || 'Save failed', type: 'error' });
      }
    } catch (err: any) {
      setStatus({ message: err.message || 'Save failed', type: 'error' });
    } finally {
      setTestLoading(false);
    }
  };

  // Delete all properties
  const deleteAllProperties = async () => {
    if (!window.confirm('Are you sure you want to delete ALL Zillow properties?')) return;

    try {
      const res = await apiFetch('/api/zillow/properties?confirm=yes', { method: 'DELETE' });
      const data = await res.json();

      if (res.ok) {
        setStatus({ message: `Deleted ${data.deleted} properties`, type: 'success' });
        fetchStats();
        fetchProperties();
      } else {
        setStatus({ message: data.error || 'Delete failed', type: 'error' });
      }
    } catch (err: any) {
      setStatus({ message: err.message || 'Delete failed', type: 'error' });
    }
  };

  // Live scrape with SSE - shows listings in real-time as each page is scraped
  const startLiveScrape = () => {
    if (selectedStates.length === 0) {
      setStatus({ message: 'Please select a state to scrape', type: 'warning' });
      return;
    }

    // Close any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    setLiveLoading(true);
    setLiveListings([]);
    setLiveProgress(null);
    setStatus(null);

    // Build the SSE URL with query params
    const params = new URLSearchParams({
      state: selectedStates[0],
      priceMin: priceMin.toString(),
      priceMax: priceMax.toString(),
      bedsMin: bedsMin.toString(),
      sqftMin: sqftMin.toString(),
      maxPages: '20',
      saveToDb: 'true',
      skipProxy: skipProxy.toString()  // Use proxy to avoid CAPTCHA (uncheck "Skip Proxy")
    });

    // Get the API base URL (from constants.ts via helpers.js)
    const apiBase = getApiBaseUrl();
    const sseUrl = `${apiBase}/api/zillow/live-scrape?${params.toString()}`;

    console.log('[LIVE SCRAPE] Connecting to SSE:', sseUrl);
    const eventSource = new EventSource(sseUrl);
    eventSourceRef.current = eventSource;

    eventSource.addEventListener('connected', (event: any) => {
      const data = JSON.parse(event.data);
      console.log('[LIVE SCRAPE] Connected:', data);
      setStatus({ message: `Connected! Starting scrape for ${data.state}...`, type: 'info' });
    });

    eventSource.addEventListener('page', (event: any) => {
      const data = JSON.parse(event.data);
      console.log(`[LIVE SCRAPE] Page ${data.pageNum}: ${data.count} listings`);

      setLiveProgress({
        currentPage: data.pageNum,
        totalPages: data.totalPages,
        totalResultCount: data.totalResultCount,
        totalScraped: data.totalScrapedSoFar || data.count
      });

      // Add new listings to the list (with deal calculation)
      const newListings = (data.listings || []).map((l: any) => ({
        ...l,
        isDeal: l.zestimate && l.price && l.zestimate >= (l.price / 0.55),
        dealRatio: l.zestimate && l.price ? (l.zestimate / l.price).toFixed(2) : null
      }));

      setLiveListings(prev => [...prev, ...newListings]);
      setStatus({
        message: `Page ${data.pageNum}/${data.totalPages}: Found ${data.count} listings (Total: ${data.totalScrapedSoFar || data.count})`,
        type: 'info'
      });
    });

    eventSource.addEventListener('complete', (event: any) => {
      const data = JSON.parse(event.data);
      console.log('[LIVE SCRAPE] Complete:', data);
      setLiveLoading(false);
      setStatus({
        message: `Scrape complete! ${data.total} listings, ${data.deals} deals, ${data.saved} saved to DB`,
        type: 'success'
      });
      eventSource.close();
      eventSourceRef.current = null;

      // Refresh stats and properties
      fetchStats();
      fetchProperties();
    });

    eventSource.addEventListener('error', (event: any) => {
      console.error('[LIVE SCRAPE] Error event:', event);
      try {
        const data = JSON.parse(event.data);
        setStatus({ message: data.message || 'Scrape error', type: 'error' });
      } catch {
        setStatus({ message: 'Connection error - check console', type: 'error' });
      }
      setLiveLoading(false);
      eventSource.close();
      eventSourceRef.current = null;
    });

    eventSource.onerror = (event) => {
      console.error('[LIVE SCRAPE] Connection error:', event);
      if (eventSource.readyState === EventSource.CLOSED) {
        setLiveLoading(false);
        eventSourceRef.current = null;
      }
    };
  };

  // Stop live scrape
  const stopLiveScrape = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setLiveLoading(false);
    setStatus({ message: 'Live scrape stopped', type: 'info' });
  };

  // RapidAPI Scrape (No CAPTCHA - uses US Property Data API)
  const startRapidApiScrape = () => {
    if (selectedStates.length === 0) {
      setStatus({ message: 'Please select a state to scrape', type: 'warning' });
      return;
    }

    // Close any existing connection
    if (rapidApiEventSourceRef.current) {
      rapidApiEventSourceRef.current.close();
    }

    setRapidApiLoading(true);
    setLiveListings([]);
    setLiveProgress(null);
    setStatus(null);

    // Build the SSE URL with query params
    const params = new URLSearchParams({
      state: selectedStates[0],
      priceMin: priceMin.toString(),
      priceMax: priceMax.toString(),
      bedsMin: bedsMin.toString(),
      sqftMin: sqftMin.toString(),
      maxPages: '20',
      saveToDb: 'true'
    });

    // Get the API base URL
    const apiBase = getApiBaseUrl();
    const sseUrl = `${apiBase}/api/zillow/rapidapi/live-scrape?${params.toString()}`;

    console.log('[RAPIDAPI SCRAPE] Connecting to SSE:', sseUrl);
    const eventSource = new EventSource(sseUrl);
    rapidApiEventSourceRef.current = eventSource;

    eventSource.addEventListener('connected', (event: any) => {
      const data = JSON.parse(event.data);
      console.log('[RAPIDAPI SCRAPE] Connected:', data);
      setStatus({ message: `Connected to RapidAPI! Fetching ${data.state}...`, type: 'info' });
    });

    eventSource.addEventListener('page', (event: any) => {
      const data = JSON.parse(event.data);
      console.log(`[RAPIDAPI SCRAPE] Page ${data.pageNum}: ${data.count} listings`);

      setLiveProgress({
        currentPage: data.pageNum,
        totalPages: data.totalPages,
        totalResultCount: data.totalResultCount,
        totalScraped: data.totalScrapedSoFar || data.count
      });

      // Add new listings to the list
      const newListings = (data.listings || []).map((l: any) => ({
        ...l,
        isDeal: l.isDeal,
        dealRatio: l.dealRatio
      }));

      setLiveListings(prev => [...prev, ...newListings]);
      setStatus({
        message: `Page ${data.pageNum}/${data.totalPages}: ${data.count} listings (${data.deals} deals) - Total: ${data.totalScrapedSoFar}`,
        type: 'info'
      });
    });

    eventSource.addEventListener('complete', (event: any) => {
      const data = JSON.parse(event.data);
      console.log('[RAPIDAPI SCRAPE] Complete:', data);
      setRapidApiLoading(false);
      setStatus({
        message: `RapidAPI scrape complete! ${data.total} listings, ${data.deals} deals, ${data.saved} saved`,
        type: 'success'
      });
      eventSource.close();
      rapidApiEventSourceRef.current = null;

      // Refresh stats and properties
      fetchStats();
      fetchProperties();
    });

    eventSource.addEventListener('error', (event: any) => {
      console.error('[RAPIDAPI SCRAPE] Error event:', event);
      try {
        const data = JSON.parse(event.data);
        if (data.error === 'QUOTA_EXCEEDED') {
          setStatus({ message: 'RapidAPI quota exceeded! Please upgrade your plan.', type: 'error' });
        } else {
          setStatus({ message: data.message || 'RapidAPI error', type: 'error' });
        }
      } catch {
        setStatus({ message: 'Connection error - check console', type: 'error' });
      }
      setRapidApiLoading(false);
      eventSource.close();
      rapidApiEventSourceRef.current = null;
    });

    eventSource.onerror = (event) => {
      console.error('[RAPIDAPI SCRAPE] Connection error:', event);
      if (eventSource.readyState === EventSource.CLOSED) {
        setRapidApiLoading(false);
        rapidApiEventSourceRef.current = null;
      }
    };
  };

  // Stop RapidAPI scrape
  const stopRapidApiScrape = () => {
    if (rapidApiEventSourceRef.current) {
      rapidApiEventSourceRef.current.close();
      rapidApiEventSourceRef.current = null;
    }
    setRapidApiLoading(false);
    setStatus({ message: 'RapidAPI scrape stopped', type: 'info' });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (rapidApiEventSourceRef.current) {
        rapidApiEventSourceRef.current.close();
      }
    };
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Zillow Scraper
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Scrape property listings from Zillow by state. Data is saved to a separate collection.
      </Typography>

      {/* Status Alert */}
      {status && (
        <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus(null)}>
          {status.message}
        </Alert>
      )}

      {/* Progress Bar */}
      {progress?.isRunning && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Scraping: {progress.currentState}
            {progress.currentPriceRange && ` - ${progress.currentPriceRange}`}
            {progress.totalPriceRanges > 1 && ` (Range ${progress.priceRangeIndex}/${progress.totalPriceRanges})`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Page {progress.currentPage}/{progress.totalPages}
            {progress.totalListingsInRange > 0 && ` (~${progress.totalListingsInRange.toLocaleString()} listings in range)`}
          </Typography>
          <LinearProgress sx={{ mb: 1 }} />
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <Typography variant="body2">Found: {progress.listingsFound}</Typography>
            <Typography variant="body2">Saved: {progress.listingsSaved}</Typography>
            <Typography variant="body2" color="success.main">Deals: {progress.dealsFound}</Typography>
            {progress.nextRangeIn !== null && progress.nextRangeIn > 0 && (
              <Typography variant="body2" color="info.main">
                Next range in: {progress.nextRangeIn}s
              </Typography>
            )}
          </Stack>
          {progress.errors.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Errors: {progress.errors.join(', ')}
            </Alert>
          )}
        </Paper>
      )}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Total Properties</Typography>
                <Typography variant="h4">{stats.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Deals Found</Typography>
                <Typography variant="h4" color="success.main">{stats.deals}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>States Scraped</Typography>
                <Typography variant="h4">{stats.byState?.length || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Search Filters</Typography>

        <Grid container spacing={2}>
          {/* State Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>States</InputLabel>
              <Select
                multiple
                value={selectedStates}
                onChange={(e) => setSelectedStates(e.target.value as string[])}
                label="States"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {US_STATES.map((s) => (
                  <MenuItem key={s.code} value={s.code}>
                    {s.code} - {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Price Range */}
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Price Min"
              type="number"
              value={priceMin}
              onChange={(e) => setPriceMin(parseInt(e.target.value) || 0)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Price Max"
              type="number"
              value={priceMax}
              onChange={(e) => setPriceMax(parseInt(e.target.value) || 0)}
            />
          </Grid>

          {/* Beds & Sqft */}
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Beds Min"
              type="number"
              value={bedsMin}
              onChange={(e) => setBedsMin(parseInt(e.target.value) || 0)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Sqft Min"
              type="number"
              value={sqftMin}
              onChange={(e) => setSqftMin(parseInt(e.target.value) || 0)}
            />
          </Grid>

          {/* Exclusions */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Exclude Property Types:</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <FormControlLabel
                control={<Checkbox checked={excludeTownhouse} onChange={(e) => setExcludeTownhouse(e.target.checked)} size="small" />}
                label="Townhouse"
              />
              <FormControlLabel
                control={<Checkbox checked={excludeCondo} onChange={(e) => setExcludeCondo(e.target.checked)} size="small" />}
                label="Condo"
              />
              <FormControlLabel
                control={<Checkbox checked={excludeLand} onChange={(e) => setExcludeLand(e.target.checked)} size="small" />}
                label="Land"
              />
              <FormControlLabel
                control={<Checkbox checked={excludeManufactured} onChange={(e) => setExcludeManufactured(e.target.checked)} size="small" />}
                label="Manufactured"
              />
              <FormControlLabel
                control={<Checkbox checked={excludeApartment} onChange={(e) => setExcludeApartment(e.target.checked)} size="small" />}
                label="Apartment"
              />
              <FormControlLabel
                control={<Checkbox checked={excludeNewConstruction} onChange={(e) => setExcludeNewConstruction(e.target.checked)} size="small" />}
                label="New Construction"
              />
              <FormControlLabel
                control={<Checkbox checked={excludeAuction} onChange={(e) => setExcludeAuction(e.target.checked)} size="small" />}
                label="Auction"
              />
              <FormControlLabel
                control={<Checkbox checked={excludeComingSoon} onChange={(e) => setExcludeComingSoon(e.target.checked)} size="small" />}
                label="Coming Soon"
              />
            </Stack>
          </Grid>

          {/* Price Range Splitting */}
          <Grid item xs={12}>
            <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={usePriceRanges}
                    onChange={(e) => setUsePriceRanges(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" component="span" fontWeight="medium">
                      Use Price Range Splitting
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Splits into 19 price ranges ($20k-$40k, $40k-$60k, etc.) with 90 second delay between each.
                      Gets more listings by bypassing Zillow's ~40 results per page limit.
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Grid>

          {/* Skip Proxy Option */}
          <Grid item xs={12}>
            <Box sx={{ p: 1.5, bgcolor: skipProxy ? 'warning.light' : 'action.hover', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipProxy}
                    onChange={(e) => setSkipProxy(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" component="span" fontWeight="medium">
                      Skip Proxy (Use Local IP)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Run without proxy rotation. Use this if proxies are getting blocked.
                      Warning: Your local IP may get rate-limited by Zillow.
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            onClick={testScrape}
            disabled={testLoading || progress?.isRunning || liveLoading}
            startIcon={<RefreshIcon />}
          >
            {testLoading ? 'Testing...' : 'Test Scrape'}
          </Button>

          {/* RapidAPI Scrape - No CAPTCHA! */}
          {!rapidApiLoading ? (
            <Button
              variant="contained"
              color="success"
              onClick={startRapidApiScrape}
              disabled={testLoading || progress?.isRunning || liveLoading || selectedStates.length === 0}
              startIcon={<CloudDownloadIcon />}
            >
              RapidAPI Scrape (No CAPTCHA)
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              onClick={stopRapidApiScrape}
              startIcon={<StopIcon />}
            >
              Stop RapidAPI
            </Button>
          )}

          {/* Live Scrape with Real-time SSE Updates (Puppeteer) */}
          {!liveLoading ? (
            <Button
              variant="outlined"
              color="secondary"
              onClick={startLiveScrape}
              disabled={testLoading || progress?.isRunning || rapidApiLoading || selectedStates.length === 0}
              startIcon={<StreamIcon />}
            >
              Puppeteer Scrape
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              onClick={stopLiveScrape}
              startIcon={<StopIcon />}
            >
              Stop Puppeteer
            </Button>
          )}

          {!progress?.isRunning ? (
            <Button
              variant="contained"
              onClick={startScrape}
              disabled={loading || selectedStates.length === 0 || liveLoading}
              startIcon={<PlayArrowIcon />}
            >
              {loading ? 'Starting...' : 'Start Scrape & Save'}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              onClick={stopScrape}
              startIcon={<StopIcon />}
            >
              Stop
            </Button>
          )}

          <Button
            variant="outlined"
            color="error"
            onClick={deleteAllProperties}
            startIcon={<DeleteIcon />}
          >
            Delete All
          </Button>
        </Stack>
      </Paper>

      {/* Live Scrape Progress & Results (RapidAPI or Puppeteer) */}
      {(liveLoading || rapidApiLoading || liveListings.length > 0) && (
        <Paper sx={{ p: 2, mb: 3, border: (liveLoading || rapidApiLoading) ? '2px solid' : 'none', borderColor: rapidApiLoading ? 'success.main' : 'secondary.main' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" color={(liveLoading || rapidApiLoading) ? (rapidApiLoading ? 'success.main' : 'secondary.main') : 'text.primary'}>
              {rapidApiLoading ? '🟢 RapidAPI Scrape In Progress (No CAPTCHA!)' : liveLoading ? '🔴 Puppeteer Scrape In Progress' : 'Scrape Results'}
              {liveProgress && ` - Page ${liveProgress.currentPage}/${liveProgress.totalPages}`}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              {liveProgress && (
                <>
                  <Chip
                    label={`${liveProgress.totalScraped} scraped`}
                    color="primary"
                    size="small"
                  />
                  <Chip
                    label={`${liveProgress.totalResultCount} total in Zillow`}
                    variant="outlined"
                    size="small"
                  />
                </>
              )}
              {(liveLoading || rapidApiLoading) && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={rapidApiLoading ? stopRapidApiScrape : stopLiveScrape}
                  startIcon={<StopIcon />}
                >
                  Stop
                </Button>
              )}
            </Stack>
          </Stack>

          {(liveLoading || rapidApiLoading) && <LinearProgress color={rapidApiLoading ? "success" : "secondary"} sx={{ mb: 2 }} />}

          <TableContainer sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Zestimate</TableCell>
                  <TableCell align="right">Ratio</TableCell>
                  <TableCell align="center">Deal</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {liveListings.map((p: any, i: number) => (
                  <TableRow key={i} sx={{ bgcolor: p.isDeal ? 'rgba(156, 39, 176, 0.1)' : 'inherit' }}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{p.address}</TableCell>
                    <TableCell align="right">{fmt(p.price)}</TableCell>
                    <TableCell align="right">{fmt(p.zestimate)}</TableCell>
                    <TableCell align="right">{p.dealRatio ? `${p.dealRatio}x` : '—'}</TableCell>
                    <TableCell align="center">{p.isDeal ? '✅' : '—'}</TableCell>
                    <TableCell>{p.beds} bd / {p.baths} ba / {p.sqft?.toLocaleString()} sqft</TableCell>
                    <TableCell>
                      {p.detailUrl && (
                        <Tooltip title="View on Zillow">
                          <IconButton
                            size="small"
                            href={`https://www.zillow.com${p.detailUrl}`}
                            target="_blank"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {liveListings.length > 0 && !liveLoading && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Total: {liveListings.length} listings | Deals: {liveListings.filter((l: any) => l.isDeal).length}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Test Results */}
      {testResults && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">
              Test Results: {testResults.state} ({testResults.total} listings, {testResults.deals} deals)
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={saveTestResults}
              disabled={testLoading || testResults.listings?.length === 0}
            >
              Save to Database
            </Button>
          </Stack>

          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Address</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Zestimate</TableCell>
                  <TableCell align="right">Ratio</TableCell>
                  <TableCell align="center">Deal</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {testResults.listings?.map((p: any, i: number) => (
                  <TableRow key={i} sx={{ bgcolor: p.isDeal ? 'success.50' : 'inherit' }}>
                    <TableCell>{p.address}</TableCell>
                    <TableCell align="right">{fmt(p.price)}</TableCell>
                    <TableCell align="right">{fmt(p.zestimate)}</TableCell>
                    <TableCell align="right">{p.dealRatio ? `${p.dealRatio}x` : '—'}</TableCell>
                    <TableCell align="center">{p.isDeal ? '✅' : '—'}</TableCell>
                    <TableCell>{p.beds} bd / {p.baths} ba / {p.sqft?.toLocaleString()} sqft</TableCell>
                    <TableCell>
                      {p.detailUrl && (
                        <Tooltip title="View on Zillow">
                          <IconButton
                            size="small"
                            href={`https://www.zillow.com${p.detailUrl}`}
                            target="_blank"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Saved Properties */}
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">Saved Properties ({properties.length})</Typography>
          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>State</InputLabel>
              <Select
                value={viewState}
                onChange={(e) => setViewState(e.target.value)}
                label="State"
              >
                <MenuItem value="all">All States</MenuItem>
                {stats?.byState?.map((s) => (
                  <MenuItem key={s._id} value={s._id}>
                    {s._id} ({s.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Checkbox checked={viewDealsOnly} onChange={(e) => setViewDealsOnly(e.target.checked)} size="small" />}
              label="Deals Only"
            />
            <IconButton onClick={fetchProperties}>
              <RefreshIcon />
            </IconButton>
          </Stack>
        </Stack>

        <TableContainer sx={{ maxHeight: 500 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Address</TableCell>
                <TableCell>State</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Zestimate</TableCell>
                <TableCell align="right">Ratio</TableCell>
                <TableCell align="center">Deal</TableCell>
                <TableCell>Details</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {properties.map((p) => (
                <TableRow key={p._id} sx={{ bgcolor: p.isDeal ? 'rgba(46, 125, 50, 0.08)' : 'inherit' }}>
                  <TableCell>{p.fullAddress}</TableCell>
                  <TableCell>{p.state}</TableCell>
                  <TableCell align="right">{fmt(p.price)}</TableCell>
                  <TableCell align="right">{fmt(p.zestimate)}</TableCell>
                  <TableCell align="right">{p.dealRatio ? `${p.dealRatio}x` : '—'}</TableCell>
                  <TableCell align="center">{p.isDeal ? '✅' : '—'}</TableCell>
                  <TableCell>{p.beds} bd / {p.baths} ba / {p.sqft?.toLocaleString()} sqft</TableCell>
                  <TableCell>
                    {p.detailUrl && (
                      <Tooltip title="View on Zillow">
                        <IconButton size="small" href={p.detailUrl} target="_blank">
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {properties.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    No properties found. Run a scrape to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
